/**
 * Core Services Registry
 *
 * Single composition root for the engine-level managers that are not owned by
 * a single React component. Every service exported here is instantiated once
 * (singleton) and wired to its collaborators, so UI commands (CommandRegistrar)
 * and tools can drive the full BIM subsystems without prop drilling.
 */

import { bimKernel } from './BIMKernel';
import { WorkplaneManager } from './WorkplaneManager';
import { SketchManager } from './SketchManager';
import { ElementFilter, FilteredElementCollector } from './ElementFilter';
import {
    SchemaBuilder,
    Entity as StorageEntity,
    FieldBuilder,
} from './ExtensibleStorage';
import { PropertySetManager } from './PropertySetManager';
import { SharedParameterRegistry } from './parameters/SharedParameterRegistry';
import { GlobalParameterManager } from './parameters/GlobalParameterManager';
import { FormulaEngine } from './FormulaEngine';
import { PhaseManager } from './PhaseManager';
import { DesignOptionManager } from './DesignOptionManager';
import { ClashDetectionManager } from './ClashDetectionManager';
import { ConstructionSequencer } from './ConstructionSequencer';
import { RevisionManager } from './RevisionManager';
import { RebarManager } from './RebarManager';
import { ElectricalSystemManager } from './ElectricalSystemManager';
import { SleeveManager } from './SleeveManager';
import { SolidJoinSolver } from './SolidJoinSolver';
import { LayerGenerator } from './geometry/LayerGenerator';
import { ViewProcessor } from './graphics/ViewProcessor';
import { GeometryExporter } from './GeometryExporter';
import { MVDValidator } from './MVDValidator';
import { ScheduleEngine } from './ScheduleEngine';
import { MEPManager as MEPNetworkManager } from './mep/MEPManager';
import { MEPManager as MEPFlowManager } from './MEPManager';
import { SystemBrowser } from './mep/SystemBrowser';
import { FittingGenerators } from './mep/FittingGenerators';
import { MEPSystemLogic } from './MEPSystemLogic';
import { AnalyticalModelManager } from './AnalyticalModelManager';
import { StructuralLoadManager } from './StructuralLoadManager';
import { FEASolver } from './FEASolver';
import { ResultsVisualization } from './ResultsVisualization';
import { FormGenerator } from '../geometry/FormGenerator';
import { ProfileManager } from '../geometry/ProfileManager';
import { FamilyLoader } from './families/FamilyLoader';
import { ParametricEngine } from './ParametricEngine';
import {
    calculateProjectCarbon,
    MATERIAL_CARBON_DB,
    exportToInsight,
} from '../tools/CarbonAnalysis';
import {
    createCableTray,
    createConduit,
    calculatePreliminaryLoads,
    CABLE_TRAY_TYPES,
} from '../tools/ElectricalTools';
import { exportToIFC, IFC_EXPORT_SETTINGS } from '../tools/IFCTools';
import { MaterialTakeoffTool } from '../tools/MaterialTakeoffTool';
import {
    createSchedule as createScheduleDef,
    generateScheduleData,
    exportScheduleToCSV,
    COMMON_SCHEDULE_FIELDS,
} from '../tools/ScheduleTool';
import {
    createSheet,
    placeViewOnSheet,
    createRevision as createSheetRevision,
    issueSheets,
    TITLE_BLOCKS,
} from '../tools/SheetTools';
import {
    createTruss,
    createBrace,
} from '../tools/StructuralTools';
import {
    SystemBalancingTool,
} from '../tools/SystemBalancingTool';
import {
    calculateAlign,
    calculateSplit,
} from '../tools/edit/ModificationTools';

// ==================== Core element access ====================

function getProject() {
    return bimKernel.getProject();
}

function getElementsList(): any[] {
    return Array.from(getProject().elements.values());
}

// ==================== Sketching / annotation planes ====================

export const workplaneManager = new WorkplaneManager();
export const sketchManager = new SketchManager();

// ==================== Selection / filtering ====================

export {
    ElementFilter,
    FilteredElementCollector,
};

export function collectElements(
    categoryIdOrName?: string
): any[] {
    const elements = getElementsList();
    if (!categoryIdOrName) return elements;
    return elements.filter(
        (el) => el.category === categoryIdOrName || el.type === categoryIdOrName
    );
}

// ==================== Parameters / formulas ====================

export const sharedParameterRegistry = new SharedParameterRegistry();
export const globalParameterManager = new GlobalParameterManager();
export const propertySetManager = new PropertySetManager();
export { FormulaEngine, SchemaBuilder, StorageEntity };
export type { FieldBuilder };

/**
 * Evaluate a family/parameter formula with unit support and feed the result
 * back into the element's parameters map.
 */
export function evaluateElementFormula(
    element: { parameters: Record<string, any> },
    targetParam: string,
    formula: string
): number | null {
    const value = FormulaEngine.resolveParameters(element.parameters, {
        [targetParam]: formula,
    });
    const resolved = value[targetParam];
    if (typeof resolved !== 'number' || !isFinite(resolved)) return null;
    element.parameters[targetParam] = resolved;
    return resolved;
}

// ==================== Phasing / design options ====================

export const phaseManager = new PhaseManager();
export const designOptionManager = new DesignOptionManager();

// ==================== Coordination ====================

export const clashDetectionManager = new ClashDetectionManager();

/**
 * Sync the clash registry with the current project elements.
 * Safe to call before every clash run; re-registers only bounds.
 */
export function syncClashRegistry(): void {
    for (const el of getElementsList()) {
        clashDetectionManager.registerFromBIMElement(el);
    }
}

// ==================== Construction sequencing / revisions ====================

export const constructionSequencer = new ConstructionSequencer();
export const revisionManager = new RevisionManager();

// ==================== Structure / analysis chain ====================

export const analyticalModelManager = new AnalyticalModelManager();
export const structuralLoadManager = new StructuralLoadManager();
export const feaSolver = new FEASolver(
    analyticalModelManager,
    structuralLoadManager
);
export const resultsVisualization = new ResultsVisualization();

/**
 * Generate the analytical model from the current physical project.
 */
export function rebuildAnalyticalModel(): void {
    analyticalModelManager.generateFromProject(getElementsList());
}

export const rebarManager = new RebarManager();
export const electricalSystemManager = new ElectricalSystemManager();

// ==================== MEP ====================

// Network-level system management (auto-detection, classification, graph)
export const mepNetworkManager = new MEPNetworkManager(getProject());
export const systemBrowser = new SystemBrowser(mepNetworkManager, getProject());
export { FittingGenerators };

// Physics/flow calculation manager (duct pressure drop, flow propagation)
export const mepFlowManager = new MEPFlowManager();
export { MEPSystemLogic };

// ==================== Fabrication / sleeves / solid joins ====================

export const sleeveManager = new SleeveManager();
export { SolidJoinSolver, LayerGenerator, ViewProcessor };

// ==================== Output / interoperability ====================

export const geometryExporter = new GeometryExporter();
export const mvdValidator = new MVDValidator();
export { FormGenerator, ProfileManager };

export const scheduleEngine = new ScheduleEngine(getElementsList);

// Default element schedule created up-front so schedule commands can run
// without additional setup.
export const defaultSchedule = scheduleEngine.createSchedule(
    'All Elements',
    'Multi-Category'
);

// ==================== Family loading ====================

export const parametricEngine = new ParametricEngine();
export const familyLoader = new FamilyLoader(parametricEngine);

// ==================== Analysis / takeoff / balancing services ============

export {
    calculateProjectCarbon,
    MATERIAL_CARBON_DB,
    exportToInsight,
    createCableTray,
    createConduit,
    calculatePreliminaryLoads,
    CABLE_TRAY_TYPES,
    exportToIFC,
    IFC_EXPORT_SETTINGS,
    createScheduleDef,
    generateScheduleData,
    exportScheduleToCSV,
    COMMON_SCHEDULE_FIELDS,
    createSheet,
    placeViewOnSheet,
    createSheetRevision,
    issueSheets,
    TITLE_BLOCKS,
    createTruss,
    createBrace,
    calculateAlign,
    calculateSplit,
};

export const materialTakeoffTool = new MaterialTakeoffTool();
export const systemBalancingTool = new SystemBalancingTool();

// ==================== Solver layer (geometry / flow / electrical) =======

import { AnalyticalModelSolver } from './AnalyticalModelSolver';
import { AttachmentSolver } from './AttachmentSolver';
import { CableTrayConduitRouter } from './CableTrayConduitRouter';
import { CircuitManager } from './CircuitManager';
import { CompoundStructureManager } from './CompoundStructureManager';
import { CoordinateSystemManager } from './CoordinateSystemManager';
import { DatabaseManager } from './DatabaseManager';
import { DuctFlowSolver } from './DuctFlowSolver';
import { FailureResolutionService } from './FailureResolutionService';
import { FamilyEditor } from './FamilyEditor';
import { FilterManager } from './FilterManager';
import { GeometricConstraintSolver } from './GeometricConstraintSolver';
import { IFCExporter } from './IFCExporter';
import { LoadManager } from './LoadManager';
import { PhasingManager } from './PhasingManager';
import { PipeFlowSolver } from './PipeFlowSolver';
import { RailingSolver } from './RailingSolver';
import { RebarShapeManager } from './RebarShapeManager';
import { ScheduleManager } from './ScheduleManager';
import { SlabGeometrySolver } from './SlabGeometrySolver';
import { SteelConnectionManager } from './SteelConnectionManager';
import { SystemInspector } from './SystemInspector';
import { TransactionManager } from './TransactionManager';
import { ViewManager } from './ViewManager';
import { ViewRangeSolver } from './ViewRangeSolver';
import { WireSolver } from './WireSolver';
import { WorksetManager } from './WorksetManager';
import { FamilyEditorSession } from './families/FamilyEditorSession';
import { InPlaceEditorContext } from './families/InPlaceEditorContext';
import { VoidProcessor } from './geometry/VoidProcessor';
import { AnnotationScaleManager } from './graphics/AnnotationScaleManager';
import { ViewFilterManager } from './graphics/ViewFilterManager';
import { MEPSectionManager } from './mep/MEPSectionManager';
import { VectorRoutingEngine } from './mep/VectorRoutingEngine';
import { VectorPrinter } from './output/VectorPrinter';
import { CSVLookupProvider } from './parameters/CSVLookupProvider';
import { StructuralLoadManager as StructureLoadManager } from './structure/StructuralLoadManager';
import { AddInManager } from './AddInManager';
import { ViewTemplateManager as OverrideViewTemplateManager } from './graphics/ViewTemplateManager';

export {
    AnalyticalModelSolver,
    AttachmentSolver,
    CableTrayConduitRouter,
    DuctFlowSolver,
    PipeFlowSolver,
    RailingSolver,
    SlabGeometrySolver,
    ViewRangeSolver,
    WireSolver,
};

export const circuitManager = new CircuitManager();
export const compoundStructureManager = new CompoundStructureManager();
export const coordinateSystemManager = new CoordinateSystemManager();
export const databaseManager = new DatabaseManager();
export const failureResolutionService = new FailureResolutionService();
export const familyEditor = new FamilyEditor();
export const filterManager = new FilterManager();
export const geometricConstraintSolver = new GeometricConstraintSolver();
export const ifcExporter = new IFCExporter();
export const loadManager = new LoadManager();
export const phasingManager = new PhasingManager();
export const rebarShapeManager = new RebarShapeManager();
export const scheduleManager = new ScheduleManager();
export const steelConnectionManager = new SteelConnectionManager();
export const systemInspector = new SystemInspector();
export const transactionManager = new TransactionManager();
export const viewManager = new ViewManager();
export const worksetManager = new WorksetManager();
export const voidProcessor = new VoidProcessor();
export const annotationScaleManager = new AnnotationScaleManager();
export const viewFilterManager = new ViewFilterManager();
export const mepSectionManager = new MEPSectionManager();
export const vectorRoutingEngine = new VectorRoutingEngine();
export const vectorPrinter = new VectorPrinter();
export const csvLookupProvider = new CSVLookupProvider();
export const structureLoadManager = new StructureLoadManager();
export const addInManager = new AddInManager();
// Override-object based template manager (distinct from the settings-based
// root ViewTemplateManager); used for quick template overrides on raw views.
export const overrideViewTemplateManager = new OverrideViewTemplateManager();
export { FamilyEditorSession, InPlaceEditorContext };

// ==================== Convenience: element source for other modules ========

export function getProjectSnapshot() {
    return getProject();
}
